/**
 * Per-user, client-side cooldown for the "Verify Your Email" auto-popup.
 *
 * The authoritative once-per-24h cadence lives in raauth (the `verification_reminder`
 * policy), but it only gates users already linked to raauth (`User.raauthId`). This
 * localStorage guard complements it: it survives sign-out/login and browser reopen, and
 * works for every user — including legacy accounts not yet linked to raauth — so the nag
 * does not re-appear on every fresh session. raauth remains the source of truth; this is
 * purely a client UX cooldown.
 *
 * All access is wrapped in try/catch and guarded against SSR, so a missing/disabled
 * localStorage simply falls back to the previous behaviour (no local window active).
 */

export const VERIFY_REMINDER_WINDOW_MS = 24 * 60 * 60 * 1000 // 24 hours

const STORAGE_PREFIX = "verifyEmailReminderShownAt:"

const storageKey = (userKey: string) => `${STORAGE_PREFIX}${userKey}`

/** Returns the epoch-ms timestamp the reminder was last shown to this user, or null. */
export function getReminderShownAt(userKey: string): number | null {
   if (typeof window === "undefined" || !userKey) return null
   try {
      const raw = window.localStorage.getItem(storageKey(userKey))
      if (!raw) return null
      const ts = Number(raw)
      return Number.isFinite(ts) ? ts : null
   } catch {
      return null
   }
}

/** True when the reminder was shown to this user within the last 24h. */
export function isReminderWindowActive(userKey: string): boolean {
   const shownAt = getReminderShownAt(userKey)
   if (shownAt === null) return false
   return Date.now() - shownAt < VERIFY_REMINDER_WINDOW_MS
}

/** Records "shown now" for this user, opening a fresh 24h cooldown. */
export function markReminderShown(userKey: string): void {
   if (typeof window === "undefined" || !userKey) return
   try {
      window.localStorage.setItem(storageKey(userKey), String(Date.now()))
   } catch {
      // Storage unavailable (private mode / quota) — fail soft.
   }
}
